import { useTranslation } from '@govard/i18n-core/react';
import { useQuery } from '@tanstack/react-query';
import { Drawer, Spin, Button, Divider, Alert } from 'antd';
import {
  ExternalLink,
  Kanban,
  User,
  Building2,
  Mail,
  Phone,
  Calendar,
  StickyNote,
  Activity,
  ChevronRight,
  Search,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ReactElement } from 'react';

import { getDealById } from '../../deals/api/deals.client.js';
import { getContactById } from '../../contacts/api/contacts.client.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';

export interface EntityOverlayItem {
  type: 'deal' | 'contact';
  id: string;
  title?: string | undefined;
}

interface EntityOverlayDrawerProps {
  item: EntityOverlayItem | null;
  onClose: () => void;
}

const STAGE_CONFIG: Record<string, { bg: string; text: string }> = {
  New: { bg: 'bg-zinc-100 text-zinc-700', text: 'text-zinc-700' },
  Contacted: { bg: 'bg-amber-50 text-amber-800', text: 'text-amber-800' },
  Negotiation: { bg: 'bg-blue-50 text-blue-800', text: 'text-blue-800' },
  Won: { bg: 'bg-emerald-50 text-emerald-800', text: 'text-emerald-800' },
  Lost: { bg: 'bg-rose-50 text-rose-800', text: 'text-rose-800' },
};

export function EntityOverlayDrawer({ item, onClose }: EntityOverlayDrawerProps): ReactElement {
  const { t, locale } = useTranslation();
  const scope = useCurrentScope();

  const dealQuery = useQuery({
    queryKey: ['deal-overlay', scope?.workspaceId, item?.id],
    queryFn: () => (item?.id ? getDealById(item.id) : Promise.reject('No ID')),
    enabled: item?.type === 'deal' && Boolean(item?.id),
    staleTime: 30_000,
  });

  const contactQuery = useQuery({
    queryKey: ['contact-overlay', scope?.workspaceId, item?.id],
    queryFn: () => (item?.id ? getContactById(item.id) : Promise.reject('No ID')),
    enabled: item?.type === 'contact' && Boolean(item?.id),
    staleTime: 30_000,
  });

  const isDeal = item?.type === 'deal';
  const deal = isDeal ? dealQuery.data : null;
  const contact = !isDeal ? contactQuery.data : null;
  const isLoading = isDeal ? dealQuery.isLoading : contactQuery.isLoading;
  const isError = isDeal ? dealQuery.isError : contactQuery.isError;
  const reloadActive = (): void => {
    void (isDeal ? dealQuery.refetch() : contactQuery.refetch());
  };

  const title = isDeal
    ? deal?.title || item?.title || t('crm.ai_chat.overlay.deal_details')
    : contact?.fullName || item?.title || t('crm.ai_chat.overlay.contact_details');

  const fullPageRoute = isDeal ? `/deals/${item?.id}` : `/contacts/${item?.id}`;

  return (
    <Drawer
      open={item !== null}
      onClose={onClose}
      size={420}
      title={
        <div className="flex items-center gap-2">
          {isDeal ? (
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700">
              <Kanban size={15} />
            </div>
          ) : (
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700">
              <User size={15} />
            </div>
          )}
          <span className="truncate text-sm font-semibold text-zinc-900">{title}</span>
        </div>
      }
      extra={
        item?.id && (
          <Link to={fullPageRoute} onClick={onClose}>
            <Button
              type="text"
              size="small"
              className="text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
            >
              <span className="text-xs font-medium">{t('crm.ai_chat.overlay.open_full_page')}</span>
              <ExternalLink size={13} className="ml-1" />
            </Button>
          </Link>
        )
      }
      className="crm-entity-overlay-drawer"
      styles={{
        header: {
          borderBottom: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
          padding: '12px 16px',
        },
        body: { padding: '16px' },
      }}
    >
      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spin />
        </div>
      ) : isError ? (
        <div
          className="flex h-48 flex-col items-center justify-center gap-3 px-6 text-center"
          data-testid="entity-overlay-error"
        >
          <Alert type="error" showIcon title={t('crm.ai_chat.overlay.load_failed')} />
          <Button size="small" onClick={reloadActive}>
            {t('crm.ai_chat.error.retry')}
          </Button>
        </div>
      ) : isDeal && deal ? (
        <div className="space-y-5 text-xs">
          {/* Quick Actions Bar */}
          <div className="flex gap-2">
            <Button
              className="flex-1 rounded-lg shadow-2xs text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              icon={<Mail size={13} />}
            >
              {t('crm.ai_chat.overlay.action_email')}
            </Button>
            <Button
              className="flex-1 rounded-lg shadow-2xs text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              icon={<StickyNote size={13} />}
            >
              {t('crm.ai_chat.overlay.action_note')}
            </Button>
            <Button
              className="flex-1 rounded-lg shadow-2xs text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              icon={<Activity size={13} />}
            >
              {t('crm.ai_chat.overlay.action_task')}
            </Button>
          </div>

          {/* General Information Section */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-zinc-900">
              {t('crm.ai_chat.overlay.general_info')}
            </h3>

            <div className="rounded-xl border border-zinc-200/80 bg-white p-3.5 shadow-2xs space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-zinc-500 font-medium">{t('crm.ai_chat.overlay.stage')}</span>
                <span
                  className={`rounded px-2 py-0.5 text-[11px] font-medium ${(STAGE_CONFIG[deal.stage] ?? STAGE_CONFIG.New ?? { bg: 'bg-zinc-100 text-zinc-700' }).bg}`}
                >
                  {t(`crm.deals.stage.${deal.stage}`) || deal.stage}
                </span>
              </div>

              {deal.amount !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500 font-medium">
                    {t('crm.ai_chat.overlay.amount')}
                  </span>
                  <span className="font-mono font-semibold text-zinc-900 text-sm">
                    {new Intl.NumberFormat(locale === 'ru' ? 'ru-RU' : 'en-US').format(
                      typeof deal.amount === 'number' ? deal.amount : Number(deal.amount) || 0,
                    )}{' '}
                    {deal.currency ?? 'RUB'}
                  </span>
                </div>
              )}

              {deal.contact && (
                <>
                  <Divider className="my-1.5 border-zinc-100" />
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500 font-medium">
                      {t('crm.ai_chat.overlay.contact')}
                    </span>
                    <Link
                      to={`/contacts/${deal.contact.id}`}
                      onClick={onClose}
                      className="flex items-center gap-1 font-medium text-zinc-800 hover:text-zinc-950 hover:underline"
                    >
                      <User size={12} className="text-zinc-400" />
                      {deal.contact.firstName} {deal.contact.lastName}
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Details Section */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-zinc-900">
              {t('crm.ai_chat.overlay.additional')}
            </h3>
            <div className="rounded-xl border border-zinc-200/80 bg-white p-3.5 shadow-2xs space-y-2.5">
              {deal.source && (
                <div className="flex items-center gap-2 text-zinc-600">
                  <span className="font-medium text-zinc-400 w-20">
                    {t('crm.ai_chat.overlay.source')}:
                  </span>
                  <span className="font-medium text-zinc-800">{deal.source}</span>
                </div>
              )}
              {deal.createdAt && (
                <div className="flex items-center gap-2 text-zinc-600">
                  <span className="font-medium text-zinc-400 w-20">
                    {t('crm.ai_chat.overlay.created_label')}
                  </span>
                  <span className="flex items-center gap-1.5 font-mono text-zinc-700">
                    <Calendar size={12} className="text-zinc-400" />
                    {new Intl.DateTimeFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
                      dateStyle: 'medium',
                    }).format(new Date(deal.createdAt))}
                  </span>
                </div>
              )}
            </div>
          </div>

          <Button
            type="primary"
            block
            size="large"
            className="rounded-xl bg-zinc-900 hover:bg-zinc-800 flex items-center justify-between px-4 mt-6 text-xs font-medium"
          >
            <Link
              to={fullPageRoute}
              onClick={onClose}
              className="text-white hover:text-white flex items-center justify-between w-full"
            >
              <span>{t('crm.ai_chat.overlay.open_full_card')}</span>
              <ChevronRight size={15} />
            </Link>
          </Button>
        </div>
      ) : !isDeal && contact ? (
        <div className="space-y-5 text-xs">
          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button
              className="flex-1 rounded-lg shadow-2xs text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              icon={<Mail size={13} />}
            >
              {t('crm.ai_chat.overlay.action_email')}
            </Button>
            <Button
              className="flex-1 rounded-lg shadow-2xs text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              icon={<Phone size={13} />}
            >
              {t('crm.ai_chat.overlay.action_call')}
            </Button>
            <Button
              className="flex-1 rounded-lg shadow-2xs text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              icon={<StickyNote size={13} />}
            >
              {t('crm.ai_chat.overlay.action_note')}
            </Button>
          </div>

          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-zinc-900">
              {t('crm.ai_chat.overlay.contacts')}
            </h3>
            <div className="rounded-xl border border-zinc-200/80 bg-white p-3.5 shadow-2xs space-y-3">
              {contact.email && (
                <div className="flex items-center gap-3 text-zinc-700">
                  <div className="flex h-7 w-7 items-center justify-center rounded-md bg-zinc-100">
                    <Mail size={13} className="text-zinc-500" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">
                      {t('crm.ai_chat.overlay.email')}
                    </span>
                    <a
                      href={`mailto:${contact.email}`}
                      className="font-medium text-zinc-900 hover:underline"
                    >
                      {contact.email}
                    </a>
                  </div>
                </div>
              )}
              {contact.phone && (
                <>
                  <Divider className="my-1 border-zinc-100" />
                  <div className="flex items-center gap-3 text-zinc-700">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-zinc-100">
                      <Phone size={13} className="text-zinc-500" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">
                        {t('crm.ai_chat.overlay.phone')}
                      </span>
                      <span className="font-mono font-medium text-zinc-900">{contact.phone}</span>
                    </div>
                  </div>
                </>
              )}
              {contact.company && (
                <>
                  <Divider className="my-1 border-zinc-100" />
                  <div className="flex items-center gap-3 text-zinc-700">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-zinc-100">
                      <Building2 size={13} className="text-zinc-500" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">
                        {t('crm.ai_chat.overlay.company')}
                      </span>
                      <span className="font-medium text-zinc-900">
                        {typeof contact.company === 'string'
                          ? contact.company
                          : (contact.company as { name?: string }).name || ''}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <Button
            type="primary"
            block
            size="large"
            className="rounded-xl bg-zinc-900 hover:bg-zinc-800 flex items-center justify-between px-4 mt-6 text-xs font-medium"
          >
            <Link
              to={fullPageRoute}
              onClick={onClose}
              className="text-white hover:text-white flex items-center justify-between w-full"
            >
              <span>{t('crm.ai_chat.overlay.open_contact_profile')}</span>
              <ChevronRight size={15} />
            </Link>
          </Button>
        </div>
      ) : (
        <div className="py-12 text-center text-zinc-400 flex flex-col items-center justify-center">
          <div className="h-10 w-10 rounded-full bg-zinc-100 flex items-center justify-center mb-2">
            <Search size={18} className="text-zinc-400" />
          </div>
          {t('crm.ai_chat.overlay.empty_details')}
        </div>
      )}
    </Drawer>
  );
}
